
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CreditCard, History, Euro, Info } from 'lucide-react';
import { Client } from '@/models/Client';
import { useTranslation } from 'react-i18next';
import { useClientPaymentHistory } from '@/hooks/clients/useClientPaymentHistory';
import PaymentHistoryDialog from './PaymentHistoryDialog';
import PaymentSummaryCard from './PaymentSummaryCard';
import SubscriptionPaymentCard from './SubscriptionPaymentCard';
import { formatLocalizedDate } from '@/utils/dateHelpers';

interface ClientPaymentSectionProps {
  client: Client;
}

const ClientPaymentSection: React.FC<ClientPaymentSectionProps> = ({
  client
}) => {
  const { t, i18n } = useTranslation();
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const { data: payments = [] } = useClientPaymentHistory(client.id);

  const getPaymentStatusColor = (status?: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'pending':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'unpaid':
      default:
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
    }
  };

  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const activeSubscriptions = client.subscriptions.filter(sub => sub.isActive);

  // Calculate subscriptions that have completed their cycle (visual indicator only)
  const subscriptionsWithCompletedCycle = activeSubscriptions.filter(sub => 
    sub.currentCycleSessionsUsed >= sub.sessionsPerMonth
  );

  // Get actual unpaid subscriptions (based on payment status, not sessions)
  const unpaidSubscriptions = activeSubscriptions.filter(sub => 
    sub.paymentStatus === 'unpaid' || sub.paymentStatus === 'partial'
  );

  console.log(`ClientPaymentSection - Cycle-based payment status calculation:`, {
    clientId: client.id,
    paymentStatus: client.paymentStatus,
    totalPaid,
    activeSubscriptions: activeSubscriptions.length,
    subscriptionDetails: activeSubscriptions.map(sub => ({
      id: sub.id,
      currentCycleSessionsUsed: sub.currentCycleSessionsUsed,
      sessionsPerMonth: sub.sessionsPerMonth,
      remainingCycleSessions: sub.sessionsPerMonth - sub.currentCycleSessionsUsed,
      hasCycleCompleted: sub.currentCycleSessionsUsed >= sub.sessionsPerMonth,
      currentCycleStartDate: sub.currentCycleStartDate,
      paymentStatus: sub.paymentStatus
    }))
  });

  return (
    <>
      <Card>
        <CardHeader className="pb-3 md:pb-6">
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <CreditCard className="h-4 w-4 md:h-5 md:w-5" />
            {t('clients.paymentStatus')}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-4 md:space-y-6">
          <div className="flex items-center justify-between">
            <span className="text-xs md:text-sm text-muted-foreground">{t('clients.status.general')}:</span>
            <Badge className={getPaymentStatusColor(client.paymentStatus)}>
              {t(`clients.${client.paymentStatus || 'unpaid'}`)}
            </Badge>
          </div>

          {/* Payment Summary Card */}
          <PaymentSummaryCard client={client} />

          {/* Payment statistics - content moved directly here */}
          <div className="space-y-1">
            <span className="text-muted-foreground">{t('clients.totalPaid')}:</span>
            <div className="font-semibold flex items-center gap-1">
              <Euro className="h-3 w-3" />
              {totalPaid.toFixed(2)}
            </div>
          </div>

          {/* Enhanced subscription status information */}
          {activeSubscriptions.length > 0 && (
            <>
              <span className="text-muted-foreground font-medium">Κατάσταση συνδρομών:</span>
              {activeSubscriptions.map(subscription => {
                const subscriptionPaymentStatus = subscription.paymentStatus || 'unpaid';
                const hasCycleCompleted = subscription.currentCycleSessionsUsed >= subscription.sessionsPerMonth;
                
                return (
                  <div key={subscription.id} className="text-xs p-2 rounded bg-slate-50 dark:bg-slate-800">
                    <div className="font-medium">
                      {subscription.workoutType?.name || 'Άγνωστος τύπος'}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="space-y-1">
                        <div className="text-muted-foreground">
                          Κύκλος: {subscription.currentCycleSessionsUsed}/{subscription.sessionsPerMonth}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          Έναρξη: {new Date(subscription.currentCycleStartDate).toLocaleDateString('el-GR')}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {hasCycleCompleted && (
                          <Badge variant="outline" className="text-xs border-orange-300 text-orange-700 bg-orange-50 dark:border-orange-600 dark:text-orange-300 dark:bg-orange-900/20">
                            Κύκλος ολοκληρώθηκε
                          </Badge>
                        )}
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            subscriptionPaymentStatus === 'paid' 
                              ? 'border-green-300 text-green-700 bg-green-50 dark:border-green-600 dark:text-green-300 dark:bg-green-900/20'
                              : subscriptionPaymentStatus === 'partial'
                              ? 'border-yellow-300 text-yellow-700 bg-yellow-50 dark:border-yellow-600 dark:text-yellow-300 dark:bg-yellow-900/20'
                              : 'border-red-300 text-red-700 bg-red-50 dark:border-red-600 dark:text-red-300 dark:bg-red-900/20'
                          }`}
                        >
                          {subscriptionPaymentStatus === 'paid' ? 'Πληρωμένη' : 
                           subscriptionPaymentStatus === 'partial' ? 'Μερική πληρωμή' : 'Απλήρωτη'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {activeSubscriptions.length === 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="text-xs">
                  Ο πελάτης δεν έχει ενεργές συνδρομές.
                </div>
              </AlertDescription>
            </Alert>
          )}

          {subscriptionsWithCompletedCycle.length === 0 && activeSubscriptions.length > 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="text-xs">
                  Δεν έχει ολοκληρωθεί κύκλος συνεδριών σε καμία συνδρομή.
                </div>
              </AlertDescription>
            </Alert>
          )}

          {unpaidSubscriptions.length > 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="text-xs">
                  {unpaidSubscriptions.length} συνδρομή/ές είναι απλήρωτες.
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          {client.lastPaymentDate && (
            <div className="text-xs text-muted-foreground">
              {t('clients.lastPaymentDate')}: {formatLocalizedDate(client.lastPaymentDate, i18n.language)}
            </div>
          )}

          {/* Individual Subscription Payment Cards - Always show if there are active subscriptions */}
          {activeSubscriptions.length > 0 && (
            <div className="space-y-4 border-t pt-4">
              <h4 className="text-sm font-medium">{t('clients.recordPayment')} - {t('clients.subscriptions')}</h4>
              <div className="grid grid-cols-1 gap-3">
                {activeSubscriptions.map(subscription => (
                  <SubscriptionPaymentCard 
                    key={subscription.id} 
                    subscription={subscription} 
                    client={client} 
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Payment History Button */}
          <div className="pt-2">
            <Button 
              onClick={() => setShowHistoryDialog(true)} 
              variant="outline" 
              size="sm" 
              className="w-full text-xs md:text-sm"
            >
              <History className="h-3 w-3 md:h-4 md:w-4 mr-2" />
              {t('clients.paymentHistory')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <PaymentHistoryDialog 
        open={showHistoryDialog} 
        onOpenChange={setShowHistoryDialog} 
        clientId={client.id} 
        clientName={`${client.firstName} ${client.lastName}`} 
      />
    </>
  );
};

export default ClientPaymentSection;
